import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { TEAM_LEADER_ROLE, TEAM_MEMBER_ROLE } from "_shared/entity-members/_views/team-roster-table";
import type { SelectionUpdate } from "work/work-agreements/teams/people-picker/_views/team-people-results-list";
import type { TeamPickerPerson } from "work/work-agreements/teams/people-picker/team-picker-person";
import type TeamPeopleSelection from "work/work-agreements/teams/people-picker/team-people-selection";

type TeamPeopleTrayProps = {
  selection: TeamPeopleSelection;
  /** Why this person may not lead the team, if they may not. */
  leaderRefusalFor: (person: TeamPickerPerson) => string | undefined;
  onSelectionChange: SelectionUpdate;
};

const Chips = styled("ul")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  listStyle: "none",
  margin: 0,
  maxHeight: 120,
  overflowY: "auto",
  padding: 0,
}));

const PersonChip = styled("li")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 20,
  display: "flex",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.25, 0.25, 0.25, 1.5),
}));

function selectedSummary(count: number): string {
  return count === 0 ? "Nobody selected" : `${count} selected`;
}

/**
 * The selection tray (#6602): everyone chosen so far, from every source, each with a Member / Leader
 * toggle. There is one leader, so making somebody leader makes whoever was chosen to lead before a
 * member — the selection keeps that rule. What becomes of the roster's own leader the picker asks
 * beneath the tray.
 */
export default function TeamPeopleTray(props: Readonly<TeamPeopleTrayProps>) {
  const { selection, leaderRefusalFor, onSelectionChange } = props;
  const leaderOnly = selection.mode === "leader";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
        <Typography variant="subtitle2" component="p" aria-live="polite">
          {selectedSummary(selection.count)}
        </Typography>
        {selection.count > 0 && (
          <Button size="small" onClick={() => onSelectionChange((current) => current.clear())}>
            Clear
          </Button>
        )}
      </Box>
      {selection.count > 0 && (
        <Chips aria-label="Selected people">
          {selection.people.map((person) => {
            const refusal = leaderRefusalFor(person);
            const role = selection.isLeader(person.userId) ? TEAM_LEADER_ROLE : TEAM_MEMBER_ROLE;
            return (
              <PersonChip key={person.userId}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {person.name}
                </Typography>
                {!leaderOnly && (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={role}
                    aria-label={`Role of ${person.name}`}
                    onChange={(_event, next: string | null) => {
                      if (next === TEAM_LEADER_ROLE) onSelectionChange((current) => current.makeLeader(person.userId));
                      if (next === TEAM_MEMBER_ROLE) onSelectionChange((current) => current.makeMember(person.userId));
                    }}
                  >
                    <ToggleButton value={TEAM_MEMBER_ROLE} sx={{ py: 0.25 }}>
                      {TEAM_MEMBER_ROLE}
                    </ToggleButton>
                    <Tooltip title={refusal ?? ""}>
                      <span>
                        {/* The span lets a disabled button still show why. The group reaches the
                            button through its context, so the span does not cost it the group. */}
                        <ToggleButton value={TEAM_LEADER_ROLE} disabled={Boolean(refusal)} sx={{ py: 0.25 }}>
                          {TEAM_LEADER_ROLE}
                        </ToggleButton>
                      </span>
                    </Tooltip>
                  </ToggleButtonGroup>
                )}
                <IconButton
                  size="small"
                  aria-label={`Remove ${person.name} from the selection`}
                  onClick={() => onSelectionChange((current) => current.remove([person.userId]))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </PersonChip>
            );
          })}
        </Chips>
      )}
    </Box>
  );
}
